/**
 * A FIFO ring bounded by BOTH entry count and an approximate byte budget, so a chatty logger
 * can never grow the diagnostics buffers without limit. Sizes are estimated by the caller (a
 * JSON-length estimate is fine); the buffer only needs a consistent order of magnitude.
 */
export class BoundedBuffer<T> {
  private readonly entries: { size: number; value: T }[] = [];
  private totalSize = 0;
  private droppedCount = 0;

  constructor(
    private readonly maxEntries: number,
    private readonly maxBytes: number,
  ) {}

  push(value: T, size: number): void {
    this.entries.push({ size, value });
    this.totalSize += size;
    while (
      this.entries.length > this.maxEntries ||
      (this.totalSize > this.maxBytes && this.entries.length > 1)
    ) {
      const dropped = this.entries.shift();
      if (dropped === undefined) {
        break;
      }
      this.totalSize -= dropped.size;
      this.droppedCount += 1;
    }
  }

  toArray(): T[] {
    return this.entries.map((entry) => entry.value);
  }

  /** The most recent `count` values, oldest first. */
  tail(count: number): T[] {
    return this.entries.slice(Math.max(0, this.entries.length - count)).map((entry) => entry.value);
  }

  get length(): number {
    return this.entries.length;
  }

  get bytes(): number {
    return this.totalSize;
  }

  /** How many values were evicted since creation / the last `clear()`. */
  get dropped(): number {
    return this.droppedCount;
  }

  clear(): void {
    this.entries.length = 0;
    this.totalSize = 0;
    this.droppedCount = 0;
  }
}
